import React from 'react';
import { connect } from 'react-redux';
import { Grid, Col, Row, Button } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { Loading } from '../../components/shared';
import { getSubmissionRevisions } from '../api';
import { downloadQuoteRevisionSubmission } from '../actions';
import { formatDollars } from '../../utils/utilities';
import PDFPreview from './PDFPreview';
import Moment from 'moment';

const QUOTE_STATUS_PENDING = 'PENDING';
const QUOTE_STATUS_REFERRED = 'REFERRED';
const QUOTE_STATUS_DRAFTED = 'DRAFTED';

class SubmissionHistoryModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            showPdf: false,
            revisions: [],
            submission: {}
        };
        this.handleHidePdf = this.handleHidePdf.bind(this);
        this.handleShowPdf = this.handleShowPdf.bind(this);
        this.formatQuoteDownload = this.formatQuoteDownload.bind(this);
        this.getRevisions = this.getRevisions.bind(this);
        this.capitalizeFirstLetter = this.capitalizeFirstLetter.bind(this);
    }

    componentDidMount = () => {
        this.setState(
            {
                loading: true
            },
            async () => {
                try {
                    const response = await getSubmissionRevisions(
                        this.props.user,
                        {
                            id: this.props.selectedSubmissionRow
                        },
                        false
                    );
                    if (response.data) {
                        const revisions = this.getRevisions(response.data);
                        this.setState({
                            loading: false,
                            submission: response.data,
                            revisions: revisions
                        });
                    }
                } catch (e) {
                    console.error(e.message);
                }
            }
        );
    };

    formatDollarsData = cell => formatDollars(cell, 'N/A', true);

    formatDateColumn = cell => Moment(cell).format('MMM Do YYYY h:mma');

    formatQuoteDownload = (cell, row) => {
        return row.transactionStatus === 'IP' ? <div>Quote not<br/>generated</div> : (
            <div>
                <Button>
                    <i
                        className="fa fa-download"
                        title="Download Quote"
                        aria-hidden="true"
                        onClick={() =>
                            this.props.dispatch(
                                downloadQuoteRevisionSubmission(
                                    this.props.user,
                                    row.id,
                                    row.revisionNumber
                                )
                            )
                        }
                    />
                </Button>
                <Button>
                    <i
                        className="fa fa-eye"
                        title="View PDF"
                        aria-hidden="true"
                        onClick={() => this.handleShowPdf(row.revisionNumber)}
                    />
                </Button>
            </div>
        );
    };

    getQuoteStatus(revision) {
        // This logic is based on the graphql-service/src/api/submission/model.js logic in the serverless project.
        const { ratings, type, clearanceStatus, quoteDrafted, transactionStatus, revisionState } = revision;
        const isReferralFailed = (ratings, type) => {
            if (!ratings || !type) {
                return false;
            }
            if (
                ratings[type] &&
                ratings[type].failedReferralRules
            ) {
                return true;
            }
            return false;
        };
        const isReferralDraft = (revisionState) => revisionState.includes('DRAFT')
        // Return status based on the following precedence: referred, drafted, and finally pending status.
        if ((!type || isReferralFailed(ratings, type)) && clearanceStatus !== 'pending' && !isReferralDraft(revisionState)) {
            return QUOTE_STATUS_REFERRED;
        }
        // Argopi version for draft status would have IP in transactionStatus
        if ((quoteDrafted === 'true' || quoteDrafted === true && transactionStatus === undefined) && clearanceStatus !== 'pending') {
            return QUOTE_STATUS_DRAFTED;
        }
        if (clearanceStatus === 'pending') {
            return QUOTE_STATUS_PENDING;
        }
        return '';
    }

    capitalizeFirstLetter(val) {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

    getRevisions(submission) {
        if (submission.length === 0) {
            return [];
        }
        const { id, revisions } = submission;
        let quoteVersion;
        let quoteVersionNumber = 0;
        return revisions
            .filter(revision => {
                const quoteStatus = this.getQuoteStatus(revision);
                if (
                    quoteStatus === QUOTE_STATUS_PENDING ||
                    quoteStatus === QUOTE_STATUS_DRAFTED ||
                    (quoteStatus === QUOTE_STATUS_REFERRED && !this.props.isUnderwriter) ||
                    Object.hasOwn(revision, 'isBinded')
                ) {
                    return false;
                }
                return true;
            })
            .map(revision => {
                const { type } = revision;
                const revisionNumber = revision.revisionNumber;
                let coverage = revision.coverages.gl.base;
                if(coverage === 'biExcluded') {
                    coverage = 'BI-Excluded'
                }
                if(coverage === 'deductibleWarranty' && (revision.manuallySetPricing === 'true' || revision.excessOnly === true)) {
                    coverage = `deductibleWarranty`
                }
                else if (coverage === 'deductibleWarranty' ) {
                    coverage = `deductibleWarranty/ <br/> Standard`
                }

                // When draft argopi version is discarded, it actually discards revision no.1.
                // Revision filter based on status(getQuoteStatus) ignores pure draft and counts draft argopi version/
                // Hence need to increment that version for correct versioning for other revisions
                if (revision.revisionState.includes('DRAFT') && revision.transactionStatus === 'IP') {
                    quoteVersion = quoteVersionNumber + 1;
                    quoteVersionNumber = quoteVersionNumber + 1;
                }
                else if (revision.revisionState.includes('DRAFT')) {
                    quoteVersion = quoteVersionNumber
                }
                else if (revision.isDeductibleWarranty  && revision.coverages.gl.base === 'deductibleWarranty' && revision.manuallySetPricing!== 'true' && revision.excessOnly !== true) {
                    quoteVersion =`${quoteVersionNumber+1}, ${quoteVersionNumber+2}`
                    quoteVersionNumber =  quoteVersionNumber+2
                }
                else {
                    quoteVersion = quoteVersionNumber +1
                    quoteVersionNumber = quoteVersionNumber +1
                }
                return {
                    totalPremium: revision.ratings[type].totalPremium,
                    effectiveDate: revision.anticipatedStartDate,
                    createdAt: revision.createdAt,
                    id,
                    revisionNumber,
                    quoteVersion,
                    coverage,
                    transactionStatus: revision.transactionStatus
                };
            })
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    handleHidePdf() {
        this.setState({
            showPDFPreview: false,
            showPdf: false
        });
    }

    handleShowPdf(revision) {
        this.setState({
            showPDFPreview: true,
            showPdf: true,
            selectedRevision: revision
        });
    }

    render() {
        let projectAddress = '';
        const submission = this.state.submission;
        if (submission && submission.projectAddress) {
            projectAddress = `${submission.projectAddress.projectAddress}, ${submission.projectAddress.projectCity}, ${submission.projectAddress.projectState}`;
        }
        return (
            <div>
                {this.state.loading && (
                    <div className="modal-loader">
                        <Loading width="44px" height="44px" />
                    </div>
                )}
                {!this.state.loading && (
                    <div className="modal-main">
                        {this.state.submission && this.state.revisions && (
                            <Grid fluid={true}>
                                <Row>
                                    <Col>
                                        <div>
                                            <label>Named Insured</label>
                                        </div>
                                        <div style={{ padding: '0 0 20px 0' }}>
                                            {this.state.submission.primaryInsuredName}
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <div>
                                            <label>Project Address</label>
                                        </div>
                                        <div style={{ padding: '0 0 20px 0' }}>
                                            {projectAddress}
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <BootstrapTable
                                            data={this.state.revisions}
                                            maxHeight="200px"
                                            maxWidth="40px"
                                            scrollTop={'Top'}
                                            bodyContainerClass="revision-table-body"
                                        >
                                            <TableHeaderColumn
                                                isKey
                                                dataField="createdAt"
                                                headerAlign="center"
                                                width='28%'
                                                dataFormat={this.formatDateColumn}
                                            >
                                                Date 
                                                <br />
                                                Quoted
                                            </TableHeaderColumn>
                                            <TableHeaderColumn
                                                dataField="quoteVersion"
                                                dataAlign="center"
                                                headerAlign="center"
                                            >
                                                Quote Version
                                                <br />
                                                Number
                                            </TableHeaderColumn>
                                            <TableHeaderColumn
                                                dataField="coverage"
                                                dataFormat={this.capitalizeFirstLetter}
                                                dataAlign="center"
                                                width='28%'
                                                headerAlign="center"
                                            >
                                                Coverage 
                                                <br />
                                                Option
                                            </TableHeaderColumn>
                                            <TableHeaderColumn
                                                dataField="totalPremium"
                                                dataFormat={this.formatDollarsData}
                                                dataAlign="center"
                                            >
                                                Total 
                                                <br/>
                                                Premium
                                            </TableHeaderColumn>
                                            <TableHeaderColumn
                                                dataFormat={this.formatQuoteDownload}
                                                headerAlign="center"
                                                dataAlign="center"
                                            >
                                                Download
                                                <br />
                                                Quote
                                            </TableHeaderColumn>
                                        </BootstrapTable>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <button
                                            className="btn-link close-button"
                                            onClick={this.props.onHide}
                                            style={{ padding: '30px 0 0 0' }}
                                        >
                                            Close and return to submissions
                                        </button>
                                    </Col>
                                </Row>
                            </Grid>
                        )}
                    </div>
                )}
                {this.state.showPDFPreview && (
                    <PDFPreview
                        custom_class="confirmationDialog"
                        submissionid={this.props.selectedSubmissionRow}
                        revision={this.state.selectedRevision}
                        show={this.state.showPdf}
                        onHide={this.handleHidePdf}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: state.user
});

const connectedSubmissionHistoryModal = connect(mapStateToProps)(SubmissionHistoryModal);

export { connectedSubmissionHistoryModal as SubmissionHistoryModal };
